export type ExplicitPublicScopeType = 'public' | 'teams' | 'department' | 'departments';

export type PublicScopeAccessSetting = {
  visibility: 'private' | 'dept' | 'team' | 'public';
  departments: string[];
  teams: string[];
};

type PublicScopeLike = {
  department_id?: unknown;
  scope_department_id?: unknown;
  scope_type?: ExplicitPublicScopeType | string | null;
  department_ids?: unknown;
  team_ids?: unknown;
};

type PublicScopeRowLike = PublicScopeLike & {
  public?: PublicScopeLike | null;
  publish_scope?: PublicScopeLike | null;
  publishScope?: PublicScopeLike | null;
};

const normalizeId = (raw: unknown): string | null => {
  const source = String(raw ?? '').trim();
  if (!/^\d+$/.test(source)) return null;
  const value = Number(source);
  return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
};

export const normalizePublicScopeIds = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  return Array.from(
    new Set(raw.map(normalizeId).filter((value): value is string => value != null))
  ).sort((a, b) => Number(a) - Number(b));
};

const readScope = (row: PublicScopeRowLike | null | undefined): PublicScopeLike => {
  if (!row) return {};
  return (
    (row.public && typeof row.public === 'object' ? row.public : null) ??
    (row.publish_scope && typeof row.publish_scope === 'object' ? row.publish_scope : null) ??
    (row.publishScope && typeof row.publishScope === 'object' ? row.publishScope : null) ??
    row
  );
};

export const buildExplicitPublicScopeAccess = (
  row: PublicScopeRowLike | null | undefined
): PublicScopeAccessSetting | null => {
  const scope = readScope(row);
  const scopeType = String(scope.scope_type ?? '').trim().toLowerCase();

  if (scopeType === 'public') {
    return { visibility: 'public', departments: [], teams: [] };
  }
  if (scopeType === 'teams') {
    return {
      visibility: 'team',
      departments: [],
      teams: normalizePublicScopeIds(scope.team_ids),
    };
  }
  if (scopeType === 'department') {
    return {
      visibility: 'dept',
      departments: normalizePublicScopeIds([scope.scope_department_id ?? scope.department_id ?? row?.department_id]),
      teams: [],
    };
  }
  if (scopeType === 'departments') {
    return {
      visibility: 'dept',
      departments: normalizePublicScopeIds(scope.department_ids),
      teams: [],
    };
  }
  return null;
};

export const canViewerAccessExplicitPublicScope = (
  setting: PublicScopeAccessSetting,
  viewer: { departmentId?: unknown; teamId?: unknown }
): boolean => {
  if (setting.visibility === 'public') return true;
  if (setting.visibility === 'private') return false;

  if (setting.visibility === 'team') {
    const teamId = normalizeId(viewer.teamId);
    return teamId != null && setting.teams.includes(teamId);
  }

  const departmentId = normalizeId(viewer.departmentId);
  return departmentId != null && setting.departments.includes(departmentId);
};
